import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Edit } from 'lucide-react';
import { Warehouse } from '@/types/warehouse.types';
import { LocationPicker } from './LocationPicker';

// Validation Schema
const editWarehouseSchema = z.object({
  code: z
    .string()
    .min(1, 'رمز المستودع مطلوب')
    .max(20, 'الرمز يجب أن يكون أقل من 20 حرف'),
  name: z
    .string()
    .min(2, 'الاسم يجب أن يكون 2 أحرف على الأقل')
    .max(100, 'الاسم يجب أن يكون أقل من 100 حرف'),
  type: z.enum(['main', 'sub'], {
    required_error: 'نوع المستودع مطلوب',
  }),
  address: z
    .string()
    .min(5, 'العنوان يجب أن يكون 5 أحرف على الأقل'),
  city_id: z.string().min(1, 'المدينة مطلوبة'),
  neighborhood_id: z.string().min(1, 'الحي مطلوب'),
  latitude: z
    .string()
    .optional()
    .transform((val) => (val ? parseFloat(val) : undefined))
    .refine((val) => val === undefined || (val >= -90 && val <= 90), {
      message: 'خط العرض يجب أن يكون بين -90 و 90',
    }),
  longitude: z
    .string()
    .optional()
    .transform((val) => (val ? parseFloat(val) : undefined))
    .refine((val) => val === undefined || (val >= -180 && val <= 180), {
      message: 'خط الطول يجب أن يكون بين -180 و 180',
    }),
  phone: z
    .string()
    .optional()
    .refine((val) => !val || /^\+?[0-9]{9,15}$/.test(val), {
      message: 'صيغة رقم الهاتف غير صحيحة',
    }),
  email: z
    .string()
    .optional()
    .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
      message: 'صيغة البريد الإلكتروني غير صحيحة',
    }),
  is_active: z.boolean(),
  is_default: z.boolean(),
});

type EditWarehouseFormValues = z.input<typeof editWarehouseSchema>;

interface City {
  id?: string;
  city_id?: string;
  name?: string | {
    en: string;
    ar: string;
  };
  name_ar?: string;
}

interface Neighborhood {
  id: string;
  name?: string;
  name_ar?: string;
}

interface EditWarehouseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  warehouse: Warehouse;
  onSuccess?: () => void;
}

export function EditWarehouseModal({
  open,
  onOpenChange,
  tenantId,
  warehouse,
  onSuccess,
}: EditWarehouseModalProps) {
  const { patch, get, loading } = useApi();
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  const form = useForm<EditWarehouseFormValues>({
    resolver: zodResolver(editWarehouseSchema),
    defaultValues: {
      code: warehouse.code || '',
      name: warehouse.name || '',
      type: warehouse.type || 'main',
      address: warehouse.address || '',
      city_id: warehouse.city_id || '',
      neighborhood_id: (warehouse as any).neighborhood_id || '',
      latitude: warehouse.latitude?.toString() || '',
      longitude: warehouse.longitude?.toString() || '',
      phone: warehouse.phone || '',
      email: warehouse.email || '',
      is_active: warehouse.is_active ?? true,
      is_default: warehouse.is_default ?? false,
    },
  });

  const selectedCityId = form.watch('city_id');

  // Fetch cities when modal opens
  useEffect(() => {
    if (open) {
      fetchCities();
    }
  }, [open]);

  // Reset form with warehouse data when modal opens or warehouse changes
  useEffect(() => {
    if (open && warehouse) {
      form.reset({
        code: warehouse.code || '',
        name: warehouse.name || '',
        type: warehouse.type || 'main',
        address: warehouse.address || '',
        city_id: warehouse.city_id || '',
        neighborhood_id: (warehouse as any).neighborhood_id || '',
        latitude: warehouse.latitude?.toString() || '',
        longitude: warehouse.longitude?.toString() || '',
        phone: warehouse.phone || '',
        email: warehouse.email || '',
        is_active: warehouse.is_active ?? true,
        is_default: warehouse.is_default ?? false,
      });
    }
  }, [open, warehouse, form]);

  // Load neighborhoods when city changes
  useEffect(() => {
    if (!selectedCityId) {
      setNeighborhoods([]);
      return;
    }
    fetchNeighborhoods(selectedCityId);
    if (selectedCityId !== warehouse.city_id) {
      form.setValue('neighborhood_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCityId]);

  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response: any = await get('/api/v1/settings/cities?lang=ar');
      if (response?.data && Array.isArray(response.data)) {
        setCities(response.data);
      } else if (response?.list && Array.isArray(response.list)) {
        setCities(response.list);
      } else if (Array.isArray(response)) {
        setCities(response);
      }
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      toast.error('فشل في جلب المدن');
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchNeighborhoods = async (cityId: string) => {
    setLoadingNeighborhoods(true);
    try {
      const response: any = await get(`/api/v1/zones?city_id=${cityId}&lang=ar`);
      const list = Array.isArray(response) ? response : (response?.data ?? []);
      setNeighborhoods(list);
    } catch (error) {
      toast.error('فشل في جلب الأحياء');
      setNeighborhoods([]);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  const onSubmit = async (values: EditWarehouseFormValues) => {
    if (!tenantId || !warehouse.id) {
      toast.error('معرف العميل أو المستودع غير موجود');
      return;
    }

    try {
      const payload: Record<string, any> = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        type: values.type,
        address: values.address.trim(),
        city_id: values.city_id,
        neighborhood_id: values.neighborhood_id,
        is_active: values.is_active,
        is_default: values.is_default,
      };

      if (values.latitude !== undefined) {
        payload.latitude = values.latitude;
      }
      if (values.longitude !== undefined) {
        payload.longitude = values.longitude;
      }
      if (values.phone) {
        payload.phone = values.phone.trim();
      } else {
        payload.phone = null;
      }
      if (values.email) {
        payload.email = values.email.trim().toLowerCase();
      } else {
        payload.email = null;
      }

      await patch(`/api/v1/dashboard/tenants/${tenantId}/warehouses/${warehouse.id}`, payload);

      toast.success('تم تحديث المستودع بنجاح');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تحديث المستودع';
      if (message.includes('already exists')) {
        toast.error('يوجد مستودع بهذا الرمز بالفعل');
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <Edit className="h-5 w-5" />
            تعديل المستودع
          </DialogTitle>
          <DialogDescription>
            تعديل بيانات المستودع "{warehouse.name}"
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                المعلومات الأساسية
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Code */}
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رمز المستودع *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="مثال: WH001"
                          className="uppercase"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>اسم المستودع *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="أدخل اسم المستودع"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type */}
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>نوع المستودع *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={loading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر النوع" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="main">رئيسي</SelectItem>
                          <SelectItem value="sub">فرعي</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* City */}
                <FormField
                  control={form.control}
                  name="city_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>المدينة *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={loading || loadingCities}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="اختر المدينة" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {cities.map((city) => {
                            const cityId = city.id || city.city_id;
                            if (!cityId) return null;

                            const label =
                              city.name_ar ||
                              (typeof city.name === 'string'
                                ? city.name
                                : city.name?.ar || city.name?.en) ||
                              '';

                            return (
                              <SelectItem key={cityId} value={cityId}>
                                {label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Neighborhood */}
              <FormField
                control={form.control}
                name="neighborhood_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>الحي *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={loading || loadingNeighborhoods || !selectedCityId}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              !selectedCityId
                                ? 'اختر المدينة أولاً'
                                : loadingNeighborhoods
                                ? 'جاري التحميل...'
                                : neighborhoods.length === 0
                                ? 'لا توجد أحياء لهذه المدينة'
                                : 'اختر الحي'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {neighborhoods.map((nb) => (
                          <SelectItem key={nb.id} value={nb.id}>
                            {nb.name_ar || nb.name || nb.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address */}
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>العنوان *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="أدخل العنوان الكامل للمستودع"
                        className="resize-none"
                        rows={2}
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                معلومات الاتصال
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Phone */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الهاتف</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>البريد الإلكتروني</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="warehouse@company.com"
                          dir="ltr"
                          className="text-start"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Location Map */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                الموقع الجغرافي
              </h3>

              <LocationPicker
                latitude={form.watch('latitude') ? parseFloat(form.watch('latitude') as string) : undefined}
                longitude={form.watch('longitude') ? parseFloat(form.watch('longitude') as string) : undefined}
                onLocationChange={(lat, lng) => {
                  form.setValue('latitude', lat.toString(), { shouldValidate: true });
                  form.setValue('longitude', lng.toString(), { shouldValidate: true });
                }}
                disabled={loading}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Latitude */}
                <FormField
                  control={form.control}
                  name="latitude"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>خط العرض</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.000001"
                          placeholder="24.7136"
                          dir="ltr"
                          className="text-start"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Longitude */}
                <FormField
                  control={form.control}
                  name="longitude"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>خط الطول</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.000001"
                          placeholder="46.6753"
                          dir="ltr"
                          className="text-start"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                الحالة
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>نشط</FormLabel>
                        <FormDescription className="text-xs">
                          المستودعات غير النشطة لا يمكن استخدامها في الشحنات
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={loading}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_default"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>افتراضي</FormLabel>
                        <FormDescription className="text-xs">
                          يستخدم تلقائياً عند إنشاء شحنات جديدة
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={loading}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                إلغاء
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="ms-2 h-4 w-4 animate-spin" />}
                حفظ التعديلات
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
